import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { GenerationModelDetails } from '../dicom/dicomBase';
import {
    DICOMApprovalStatus,
    DicomStructureSet,
    Roi,
} from '../dicom/dicomstructureset';
import { Modality } from '../dicom/modality';
import {
    DicomProcessor,
    dcmjsRemoveStructure,
} from '../services/dicomProcessor';
import { Image3D } from './image3d';

export class StructureSet implements Iterable<Roi> {
    public frameOfReferenceUid: string;

    /**
     * Whether the structure set is visible in the viewer.
     * @default true
     */
    private _visible: boolean = true;

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(visible: boolean) {
        this._visible = visible;
    }

    /**
     * The approval status of the whole structure set.
     */
    public get approvalStatus(): DICOMApprovalStatus {
        return this.dicomStructureSet.approvalStatus;
    }

    public get reviewerName(): string {
        return this.dicomStructureSet.reviewerName;
    }

    public get structureSetLabel(): string {
        return this.dicomStructureSet.structureSetLabel;
    }

    public set structureSetLabel(label: string) {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetLabel = label;
    }

    public get structureSetDescription(): string {
        return this.dicomStructureSet.structureSetDescription;
    }

    public set structureSetDescription(description: string) {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetDescription = description;
    }

    public get dicomUid(): string {
        return this.dicomStructureSet.sopInstanceUID;
    }

    public get manufacturer(): string {
        return this.dicomStructureSet.manufacturer;
    }

    public get manufacturerModelName(): string {
        return this.dicomStructureSet.manufacturerModelName;
    }

    public get softwareVersions(): string {
        return this.dicomStructureSet.softwareVersions;
    }

    /**
     * Get an ROI by its roiNumber.
     * @param roiNumber - The number of the ROI you want to retrieve.
     * @returns The corresponding ROI, or undefined if not found.
     */
    public getRoiByNumber(roiNumber: number): Roi | undefined {
        return this.dicomStructureSet.rois.get(roiNumber);
    }

    /**
     * Get all ROIs with a specific approval status.
     * @param approvalStatus - The approval status to filter by.
     * @returns An array of ROIs with the specified approval status.
     *
     */
    public getRoiByApprovalStatus(approvalStatus: DICOMApprovalStatus): Roi[] {
        return this.roiArray.filter(
            (roi) => roi.approvalStatus === approvalStatus
        );
    }

    /**
     * Map where key is the {@link Roi.roiNumber} and value is the {@link Roi}.
     */
    public get rois(): ReadonlyMap<number, Roi> {
        return this.dicomStructureSet.rois;
    }

    public get roiArray(): Roi[] {
        return Array.from(this.dicomStructureSet.rois.values());
    }

    public get image(): Image3D | null {
        return this._image;
    }

    public set image(image: Image3D | null) {
        this._image = image;
    }

    public get numberOfStructures(): number {
        return this.dicomStructureSet.numberOfStructures;
    }

    public deleteRoi(roiNumber: number): void {
        this._ensureCanModify();
        this.dicomStructureSet.deleteRoi(roiNumber);
    }

    public setStructureSetApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string
    ): {
        approvalStatus: DICOMApprovalStatus;
        reviewerName: string;
        reviewDate: string;
        reviewTime: string;
    } {
        this.dicomStructureSet.setStructureSetApprovalStatus(
            approvalStatus,
            reviewerName
        );
        return {
            approvalStatus: this.dicomStructureSet.approvalStatus,
            reviewerName: this.dicomStructureSet.reviewerName,
            reviewDate: this.dicomStructureSet.reviewDate,
            reviewTime: this.dicomStructureSet.reviewTime,
        };
    }

    public setRoisApprovalStatus(newStatus: DICOMApprovalStatus): void {
        this.dicomStructureSet.setRoisApprovalStatus(newStatus);
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public exportWith(roiNumbers: number[], characterSet?: string): Blob {
        const dicomClone = new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(this.dicomStructureSet)
        );
        if (characterSet) {
            dicomClone.setCharacterSet(characterSet);
        }
        const allRois = this.dicomStructureSet.rois.keys();
        for (const roiNumber of allRois) {
            if (!roiNumbers.includes(roiNumber)) {
                dcmjsRemoveStructure(dicomClone, roiNumber);
            }
        }
        return dicomClone.asBlob();
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomStructureSet.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomStructureSet.isNew;
    }

    public get isExported(): boolean {
        return this.dicomStructureSet.isExported;
    }

    public setExported(): void {
        this.dicomStructureSet.setExported();
    }

    public export(): Blob {
        return this.dicomStructureSet.datasetWrapper.asBlob();
    }
    /**
     * Exports the structure set as a Monaco DIR file.
     * This is a custom format used for sharing structure sets in a specific way.
     * @param patientName - The name of the patient, used in the export.
     * @returns A Blob representing the exported structure set in Monaco format.
     */
    public exportAsMonacoFile(patientName: string): Blob {
        if (!this.image) {
            throw new Error(
                'Cannot export Monaco file without an associated image.'
            );
        }

        const modality = this.image.seriesMetadata.modality;

        if (modality !== Modality.CT && modality !== Modality.MR) {
            throw new Error(
                'Can only export Monaco file for CT or MR modalities.'
            );
        }
        const formatOrientation = (num: number): string => {
            if (num === 0) {
                return '0';
            }
            if (num === 1) {
                return '1';
            }
            if (num === -1) {
                return '-1';
            }
            return num.toFixed(6);
        };

        const imagePositionLines = Array.from(
            this.image.firstSlice.imagePosition
        ).map((x) => x.toFixed(4));
        // export only the X and Y direction cosines as defined in DICOM
        const imageOrientationLines = Array.from(
            this.image.firstSlice.imageOrientation.slice(0, 6) // Only take the first 6 elements for orientation
        ).map(formatOrientation);

        const monacoDirFileLines: (string | number)[] = [
            '!Version=2',
            modality,
            this.image.sopClassUid,
            this.image.seriesMetadata.seriesInstanceUID,
            this.image.firstSlice.studyInstanceUID,
            'NO_ID',
            patientName,
            this.image.frameOfReferenceUid,
            ...imagePositionLines,
            ...imageOrientationLines,
            'RTSTRUCT',
            '1.2.840.10008.5.1.4.1.1.481.3', // DICOMClassSopUIDs.rt_struct.value
            this.dicomStructureSet.sopInstanceUID,
            this.dicomStructureSet.seriesInstanceUid,
            'NO_ID',
            this.structureSetDescription || 'NO_DESCRIPTION',
            '',
            '',
            '',
            '',
            0,
            0,
            0,
            0,
            0,
            0,
        ];

        const monacoDirFileContent = monacoDirFileLines.join('\n') + '\n';

        return new Blob([monacoDirFileContent], {
            type: 'text/plain;charset=utf-8',
        });
    }

    /**
     * Get all ROIs with a specific visibility.
     * @param visibility - The visibility to filter by.
     * @returns An array of ROIs with the specified visibility.
     */
    public getRoiByVisibility(visibility: boolean): Roi[] {
        return this.roiArray.filter(
            (roi) => Boolean(roi.visible) === visibility
        );
    }

    /*
     * Set the visibility of all ROIs.
     * @param visibility - The visibility to set.
     */
    public setRoisVisibility(visibility: boolean): void {
        this.dicomStructureSet.setRoisVisibility(visibility);
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    constructor(
        public readonly dicomStructureSet: DicomStructureSet,
        frameOfReferenceUid: string,
        private _image: Image3D | null
    ) {
        this.frameOfReferenceUid = frameOfReferenceUid;
    }

    public [Symbol.iterator](): Iterator<Roi> {
        return this.dicomStructureSet[Symbol.iterator]();
    }
}
